import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout/Layout';
import { Login } from '../pages/Login/Login';
import { Dashboard } from '../pages/Dashboard/Dashboard';
import { UserManagement } from '../pages/Admin/UserManagement';
import { ClassManagement } from '../pages/Admin/ClassManagement';
import { SubjectManagement } from '../pages/Admin/SubjectManagement';
import { FacultySubjectAssignmentEnhanced } from '../pages/Admin/FacultySubjectAssignmentEnhanced';
import { DataMigration } from '../pages/Admin/DataMigration';
import { SubjectWiseAttendance } from '../pages/Admin/SubjectWiseAttendance';
import { AttendanceMarking } from '../pages/Faculty/AttendanceMarking';
import { SubjectAttendanceMarking } from '../pages/Faculty/SubjectAttendanceMarking';
import { SubjectAttendanceView } from '../pages/Faculty/SubjectAttendanceView';
import { StudentSubjectAttendance } from '../pages/Faculty/StudentSubjectAttendance';
import { FacultyDashboard } from '../pages/Faculty/FacultyDashboard';
import { Reports } from '../pages/Reports/Reports';
import { Analytics } from '../pages/Analytics/Analytics';
import { Settings } from '../pages/Settings/Settings';
import { Profile } from '../pages/Profile/Profile';
import { NotFound } from '../pages/NotFound/NotFound';
import { AppDownload } from '../pages/AppDownload';
import { LoadingScreen } from '../components/UI/LoadingScreen';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (roles && user && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      
      {/* App Download - accessible to everyone */}
      <Route path="/app-download" element={<AppDownload />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Default redirect */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        
        {/* Dashboard - accessible to all authenticated users */}
        <Route path="dashboard" element={<Dashboard />} />
        
        {/* Profile - accessible to all authenticated users */}
        <Route path="profile" element={<Profile />} />
        
        {/* Admin Routes */}
        <Route
          path="admin"
          element={
            <ProtectedRoute roles={['admin']}>
              <Navigate to="/admin/users" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <ProtectedRoute roles={['admin']}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/classes"
          element={
            <ProtectedRoute roles={['admin']}>
              <ClassManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/subjects"
          element={
            <ProtectedRoute roles={['admin']}>
              <SubjectManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/faculty-subject-assignment"
          element={
            <ProtectedRoute roles={['admin']}>
              <FacultySubjectAssignmentEnhanced />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/data-migration"
          element={
            <ProtectedRoute roles={['admin']}>
              <DataMigration />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/subject-attendance"
          element={
            <ProtectedRoute roles={['admin']}>
              <SubjectWiseAttendance />
            </ProtectedRoute>
          }
        />
        
        {/* Faculty Routes */}
        <Route
          path="faculty"
          element={
            <ProtectedRoute roles={['faculty', 'admin']}>
              <Navigate to="/faculty/dashboard" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="faculty/dashboard"
          element={
            <ProtectedRoute roles={['faculty', 'admin']}>
              <FacultyDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="faculty/attendance"
          element={
            <ProtectedRoute roles={['faculty', 'admin']}>
              <AttendanceMarking />
            </ProtectedRoute>
          }
        />
        <Route
          path="faculty/subject-attendance"
          element={
            <ProtectedRoute roles={['faculty', 'admin']}>
              <SubjectAttendanceMarking />
            </ProtectedRoute>
          }
        />
        <Route
          path="faculty/subject-attendance-view"
          element={
            <ProtectedRoute roles={['faculty', 'admin']}>
              <SubjectAttendanceView />
            </ProtectedRoute>
          }
        />
        <Route
          path="faculty/student-subject-attendance"
          element={
            <ProtectedRoute roles={['faculty', 'admin']}>
              <StudentSubjectAttendance />
            </ProtectedRoute>
          }
        />
        
        {/* Reports - accessible to all roles with different permissions */}
        <Route path="reports" element={<Reports />} />
        
        {/* Analytics - accessible to all roles with different permissions */}
        <Route path="analytics" element={<Analytics />} />
        
        {/* Settings */}
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* 404 Route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
} 